import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Grid, Tooltip } from '@material-ui/core';

import { getDefaultLanguage } from '@/packages/layout/Sidebar/config';
import { useAppParams } from '@klp/core/app';
import { ActionButton, Block, Heading } from '@klp/design';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Loader } from '@klp/design/Loader';
import type { ITableConsumerState } from '@klp/design/Table';
import { Table, TableColumnDef, TableFilterDef } from '@klp/design/Table';
import { useKlpStyles } from '@klp/design/Theme';
import { Main } from '@klp/layout';
import { Search, filterSearchResult } from '@klp/search';

import { MessageCategory, MessageStatus } from '../classes';
import type { Message } from '../classes';
import { MessageBoardRoutes } from '../config';
import { useGetMessageListQuery } from '../rtkApi';

import MenuPopper from './MenuPopper';

const styles = () => ({
    statusIcon: {
        display: 'block',
        margin: 'auto',
    },
    button: {
        float: 'right',
    },
    recipientCells: {
        textOverflow: 'ellipsis',
        maxWidth: '300px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textAlign: 'left',
    },
    titleCells: {
        textOverflow: 'ellipsis',
        maxWidth: '400px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textAlign: 'left',
    },
    alignCenter: {
        textAlign: 'center',
    },
});

export const MessageList: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const history = useHistory();
    const params = useAppParams();

    const { isAdmin, selectedClient } = params;

    const { data, isLoading: loading } = useGetMessageListQuery({
        isAdmin,
        selectedKunde: selectedClient?.kundeNummer,
        region: getDefaultLanguage(),
    });

    const messageList = data?.messageList || [];

    // Local state
    const [search, setSearch] = useState('');

    const handleAddClick = () => {
        history.push(MessageBoardRoutes.add());
    };

    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);
    };

    const renderMessageCategory = (value: string) => {
        const { Warning, Alert, Info } = MessageCategory;
        switch (value) {
            case Info:
                return (
                    <Tooltip title={t('message.categoryType.Info')}>
                        <Illustration
                            icon={IconName.MAIL}
                            className={classes.statusIcon}
                            klpColor="medium"
                        />
                    </Tooltip>
                );
            case Warning:
                return (
                    <Tooltip title={t('message.categoryType.Warning')}>
                        <Illustration
                            icon={IconName.CIRCLE_OBS}
                            className={classes.statusIcon}
                            klpPalette="primary"
                        />
                    </Tooltip>
                );
            case Alert:
                return (
                    <Tooltip title={t('message.categoryType.Alert')}>
                        <Illustration
                            icon={IconName.ALERT}
                            className={classes.statusIcon}
                            klpPalette="secondary"
                        />
                    </Tooltip>
                );
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    };

    const renderMessageStatus = (value: string): JSX.Element => (
        <>{t(`message.status.${value}`)}</>
    );

    const renderTitle = (value: string): JSX.Element => (
        <Block klpTypographyVariant="small" className={classes.titleCells}>
            {value}
        </Block>
    );

    const renderRecipientName = <DataType, FilterType>(
        value: string,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: Message,
    ): JSX.Element => {
        const formatedList = row.mottakereList
            .map((recipient) => recipient.kundeNavn)
            .join(', ');

        return (
            <Tooltip title={formatedList}>
                <Block
                    klpTypographyVariant="small"
                    className={classes.recipientCells}
                >
                    {formatedList}
                </Block>
            </Tooltip>
        );
    };

    const renderHandlingsmeny = <DataType, FilterType>(
        value: boolean,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: Message,
    ): JSX.Element => {
        return (
            <div>
                {row.id && (
                    <Block
                        className={classes.alignCenter}
                        justifyContent="center"
                    >
                        <div
                            onClick={(event: React.MouseEvent) =>
                                event.stopPropagation()
                            }
                        >
                            <MenuPopper
                                handleData={{
                                    messageId: row.id,
                                }}
                            />
                        </div>
                    </Block>
                )}
            </div>
        );
    };

    const handleMessageSelect = (message: Message) => {
        history.push(MessageBoardRoutes.single(message.id));
    };

    const { Draft, Published, Expired, Deleted, WillPublish } = MessageStatus;

    if (!messageList && !loading) {
        return null;
    }

    const filteredList =
        messageList &&
        filterSearchResult<Message>(messageList, search, ['_content', 'title']);

    const tableDef = [
        new TableColumnDef({
            key: 'category',
            translation: 'message.category.name',
            align: 'left',
            render: renderMessageCategory,
        }),
        new TableColumnDef({
            key: 'title',
            translation: 'message.title.name',
            align: 'left',
            render: renderTitle,
        }),
        new TableColumnDef({
            key: 'createdAt',
            translation: 'message.publishAt.name',
            align: 'left',
            type: 'date',
            sortable: true,
        }),
    ];
    if (isAdmin) {
        tableDef.push(
            new TableColumnDef({
                key: 'adminStatus',
                translation: 'message.messageStatus',
                align: 'left',
                render: renderMessageStatus,
                sortable: true,
            }),
        );
    }

    if (!selectedClient) {
        tableDef.push(
            new TableColumnDef({
                key: 'mottakereList',
                translation: 'buildingClientChooser.recipients.select',
                render: renderRecipientName,
            }),
        );
    }
    if (isAdmin) {
        tableDef.push(
            new TableColumnDef({
                key: 'action',
                translation: 'message.action',
                align: 'left',
                render: renderHandlingsmeny,
            }),
        );
    }

    return (
        <Main>
            <Loader isLoading={loading}>
                {isAdmin && selectedClient && (
                    <Heading
                        translation="message.messagesFor"
                        translationOptions={{
                            kundeName: selectedClient.kundeName,
                            kundeNummer: selectedClient.kundeNummer,
                        }}
                        marginBottom="bulky"
                    />
                )}
                {isAdmin && !selectedClient && (
                    <Heading
                        translation="message.allMessages"
                        marginBottom="bulky"
                    />
                )}
                {!isAdmin && (
                    <Heading
                        translation="message.myMessages"
                        marginBottom="bulky"
                    />
                )}
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} sm={6} md={4} lg={3}>
                        <Search
                            id="messageSearch"
                            searchHandler={handleSearch}
                            searchOnType={true}
                            placeholder="sak.searchSak"
                        />
                    </Grid>
                    {isAdmin && (
                        <Grid item={true} xs={12} sm={6} md={8} lg={9}>
                            <Grid container={true} justifyContent="flex-end">
                                <Grid item={true}>
                                    <ButtonsContainer>
                                        <ActionButton
                                            className={classes.button}
                                            actionType="forward"
                                            translation="message.addMessage"
                                            clickHandler={handleAddClick}
                                        />
                                    </ButtonsContainer>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>

                {isAdmin && (
                    <Table
                        id="MessageListAdmin"
                        emptyTableMessage="message.noMessages"
                        data={filteredList ?? []}
                        filter={[
                            new TableFilterDef<MessageStatus>({
                                filterKey: 'adminStatus',
                                filterGroup: {
                                    Draft: [Draft],
                                    Published: [Published],
                                    Expired: [Expired],
                                    Deleted: [Deleted],
                                    WillPublish: [WillPublish],
                                },
                                translations: {
                                    Draft: 'message.status.Draft',
                                    Published: 'message.status.Published',
                                    Expired: 'message.status.Expired',
                                    Deleted: 'message.status.Deleted',
                                    WillPublish: 'message.status.WillPublish',
                                },
                            }),
                        ]}
                        activeFilter={[Published]}
                        columns={tableDef}
                        onRowSelect={handleMessageSelect}
                        initialRowsPerPage={10}
                        sortBy={tableDef[2]}
                        sortByDirection="desc"
                    />
                )}
                {!isAdmin && (
                    <Table
                        id="MessageListUser"
                        emptyTableMessage="message.noMessages"
                        data={filteredList ?? []}
                        columns={tableDef}
                        onRowSelect={handleMessageSelect}
                        initialRowsPerPage={10}
                        sortBy={tableDef[2]}
                        sortByDirection="desc"
                    />
                )}
            </Loader>
        </Main>
    );
};

MessageList.displayName = 'MessageList';
